import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './VisionDisplay.css';

const VisionDisplay = ({ activity, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!activity || !isOpen) return null;

  // Mock additional data for the expanded view
  const extendedData = {
    overview: {
      status: 'In Progress',
      startTime: '9:30 AM',
      estimatedCompletion: '10:45 AM',
      priority: 'High',
      successRate: '87%'
    },
    prospect: {
      name: activity.who.name,
      title: activity.who.title,
      company: 'OpenAI',
      email: 'sam@openai.com',
      linkedIn: '/in/samaltman',
      lastContact: '2 weeks ago',
      engagementScore: 8.5,
      interests: ['AI Safety', 'AGI Development', 'Startup Ecosystem']
    },
    communication: {
      emails: [
        { subject: 'Introduction to Convert AI', status: 'sent', time: '9:35 AM' },
        { subject: 'Re: AI-Powered Sales Solutions', status: 'draft', time: '9:45 AM' },
        { subject: 'Follow-up: Demo Request', status: 'scheduled', time: '11:00 AM' }
      ],
      notes: [
        'Interested in AI-powered sales automation',
        'Prefers technical discussions',
        'Decision maker for new tools adoption'
      ]
    },
    activity: {
      current: activity.what,
      steps: [
        { step: 'Research Profile', status: 'completed', time: '9:30 AM' },
        { step: 'Craft Personalized Message', status: 'completed', time: '9:35 AM' },
        { step: 'Send Initial Outreach', status: 'in-progress', time: '9:40 AM' },
        { step: 'Schedule Follow-up', status: 'pending', time: '10:00 AM' }
      ]
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="vision-display-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="vision-display-container"
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="vision-header">
            <div className="vision-header-content">
              <div className="vision-title-section">
                <h2 className="vision-title">Activity Working Canvas</h2>
                <div className="vision-status-badge">
                  <span className="status-indicator active"></span>
                  <span>{extendedData.overview.status}</span>
                </div>
              </div>
              <button className="vision-close-btn" onClick={onClose} aria-label="Close">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            
            {/* Tabs */}
            <div className="vision-tabs">
              <button
                className={`vision-tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
              >
                Overview
              </button>
              <button
                className={`vision-tab ${activeTab === 'prospect' ? 'active' : ''}`}
                onClick={() => setActiveTab('prospect')}
              >
                Prospect Details
              </button>
              <button
                className={`vision-tab ${activeTab === 'communication' ? 'active' : ''}`}
                onClick={() => setActiveTab('communication')}
              >
                Communication
              </button>
              <button
                className={`vision-tab ${activeTab === 'activity' ? 'active' : ''}`}
                onClick={() => setActiveTab('activity')}
              >
                Activity Timeline
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="vision-content">
            <AnimatePresence mode="wait">
              {activeTab === 'overview' && (
                <motion.div
                  key="overview"
                  className="vision-panel"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="vision-grid">
                    <div className="vision-card">
                      <div className="card-icon">⏰</div>
                      <div className="card-content">
                        <span className="card-label">Started</span>
                        <span className="card-value">{extendedData.overview.startTime}</span>
                      </div>
                    </div>
                    <div className="vision-card">
                      <div className="card-icon">🎯</div>
                      <div className="card-content">
                        <span className="card-label">Est. Completion</span>
                        <span className="card-value">{extendedData.overview.estimatedCompletion}</span>
                      </div>
                    </div>
                    <div className="vision-card">
                      <div className="card-icon">⚡</div>
                      <div className="card-content">
                        <span className="card-label">Priority</span>
                        <span className="card-value priority-high">{extendedData.overview.priority}</span>
                      </div>
                    </div>
                    <div className="vision-card">
                      <div className="card-icon">📊</div>
                      <div className="card-content">
                        <span className="card-label">Success Rate</span>
                        <span className="card-value">{extendedData.overview.successRate}</span>
                      </div>
                    </div>
                  </div>

                  <div className="vision-main-card">
                    <h3>Current Activity</h3>
                    <div className="activity-display">
                      <div className="activity-icon-large">📧</div>
                      <div className="activity-details">
                        <h4>{activity.what}</h4>
                        <p>Engaging with {activity.who.name} at {activity.where}</p>
                        <div className="activity-progress">
                          <div className="progress-header">
                            <span>Progress</span>
                            <span>{activity.progress}%</span>
                          </div>
                          <div className="progress-track">
                            <div className="progress-bar-fill" style={{ width: `${activity.progress}%` }}></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'prospect' && (
                <motion.div
                  key="prospect"
                  className="vision-panel"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="prospect-header">
                    <div className="prospect-avatar-large">
                      {activity.who.avatar}
                    </div>
                    <div className="prospect-info">
                      <h3>{extendedData.prospect.name}</h3>
                      <p>{extendedData.prospect.title}</p>
                      <p className="company">{extendedData.prospect.company}</p>
                    </div>
                    <div className="engagement-score">
                      <span className="score-label">Engagement Score</span>
                      <span className="score-value">{extendedData.prospect.engagementScore}/10</span>
                    </div>
                  </div>

                  <div className="prospect-details">
                    <div className="detail-row">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{extendedData.prospect.email}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">LinkedIn</span>
                      <span className="detail-value">{extendedData.prospect.linkedIn}</span>
                    </div>
                    <div className="detail-row">
                      <span className="detail-label">Last Contact</span>
                      <span className="detail-value">{extendedData.prospect.lastContact}</span>
                    </div>
                  </div>

                  <div className="interests-section">
                    <h4>Key Interests</h4>
                    <div className="interests-tags">
                      {extendedData.prospect.interests.map((interest, i) => (
                        <span key={i} className="interest-tag">{interest}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'communication' && (
                <motion.div
                  key="communication"
                  className="vision-panel"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="comm-section">
                    <h3>Email Communications</h3>
                    <div className="email-list">
                      {extendedData.communication.emails.map((email, i) => (
                        <div key={i} className="email-item">
                          <div className={`email-status ${email.status}`}></div>
                          <div className="email-content">
                            <span className="email-subject">{email.subject}</span>
                            <span className="email-time">{email.time}</span>
                          </div>
                          <span className={`email-badge ${email.status}`}>{email.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="comm-section">
                    <h3>Important Notes</h3>
                    <div className="notes-list">
                      {extendedData.communication.notes.map((note, i) => (
                        <div key={i} className="note-item">
                          <span className="note-icon">📝</span>
                          <span className="note-text">{note}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'activity' && (
                <motion.div
                  key="activity"
                  className="vision-panel"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="timeline-container">
                    <h3>Activity Timeline</h3>
                    <div className="timeline">
                      {extendedData.activity.steps.map((step, i) => (
                        <div key={i} className={`timeline-item ${step.status}`}>
                          <div className="timeline-marker">
                            <span className={`marker ${step.status}`}></span>
                            <div className="timeline-line"></div>
                          </div>
                          <div className="timeline-content">
                            <div className="timeline-header">
                              <span className="timeline-title">{step.step}</span>
                              <span className="timeline-time">{step.time}</span>
                            </div>
                            <span className={`timeline-status ${step.status}`}>
                              {step.status.replace('-', ' ')}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer Actions */}
          <div className="vision-footer">
            <button className="vision-action-btn secondary" onClick={onClose}>
              Close
            </button>
            <button className="vision-action-btn primary">
              View Full Details
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default VisionDisplay;
